import { Component, Element, Host, Prop, State, h } from '@stencil/core';
import { uniqueId } from '../../helpers/helpers';
import type { AccordionData } from '../../helpers/utils/accordion-types';
import type { ResponsiveImages } from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-accordion-row',
  styleUrl: 'cms-accordion-row.scss',
  shadow: true,
})
export class CmsAccordionRow {
  private item: HTMLElement;
  private accordionTrigger: HTMLElement;
  private accordionPanel: HTMLElement;
  private animation: Animation;
  private isClosing = false;
  private isExpanding = false;
  @State() isOpen = false;

  /**
   * Accordion data
   */
  @Prop() contentData: AccordionData;

  /**
   * control the open state of the first accordion Item
   */
  @Prop() firstItemIsOpen: boolean;

  /**
   * reference the accordion item element
   */
  @Element() accordionItemElement: HTMLElement;

  componentWillLoad() {
    this.isOpen = this.firstItemIsOpen === true;
  }

  componentDidLoad() {
    this.item =
      this.accordionItemElement.shadowRoot.querySelector('.accordion-item');
    this.accordionTrigger =
      this.accordionItemElement.shadowRoot.querySelector('.accordion-trigger');
    this.accordionPanel =
      this.accordionItemElement.shadowRoot.querySelector('.accordion-panel');

    if (!this.isOpen) {
      this.item.style.height = `${this.accordionTrigger.offsetHeight}px`;
    }
  }

  private renderAccordionRow() {
    if (!this.contentData) return;

    const panelId = uniqueId();
    return (
      <div class='accordion-item'>
        <button
          onClick={(event: Event) => this.triggerAccordion(event)}
          onKeyDown={(event: KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
              this.triggerAccordion(event);
            }
          }}
          type='button'
          aria-expanded={this.isOpen ? 'true' : 'false'}
          class='accordion-trigger'
          aria-controls={`panel-id-${panelId}`}
          id={`accordion-id-${panelId}`}
        >
          <span
            aria-label={this.contentData.accordionHeadline ?? ''}
            class='accordion-title'
            innerHTML={this.contentData.accordionHeadline ?? ''}
          />
          <span class='accordion-icon'>
            <cms-icon
              size='md'
              icon={this.isOpen ? 'countMinus' : 'countPlus'}
              class='accordion-icon-svg'
            />
          </span>
        </button>

        <section
          aria-hidden={!this.isOpen}
          id={`panel-id-${panelId}`}
          aria-labelledby={`accordion-id-${panelId}`}
          class='accordion-panel'
        >
          <div>
            <cms-text-image
              layout='full'
              content={this.contentData.accordionContent.content ?? ''}
              picturePosition={
                this.contentData.accordionContent.picturePosition ?? 'top'
              }
              pictureAlt={this.contentData.accordionContent.pictureAlt ?? ''}
              pictureDescription={
                this.contentData.accordionContent.pictureDescription ?? ''
              }
              images={this.checkImages(
                this.contentData.accordionContent.images,
              )}
            />
          </div>
        </section>
      </div>
    );
  }

  private checkImages(images: ResponsiveImages) {
    if (images && images?.bp320 !== '') {
      return JSON.stringify(images);
    }
  }

  private triggerAccordion(event: Event) {
    event.preventDefault();
    this.item.style.overflow = 'hidden';

    if (this.isClosing || this.isExpanding) {
      return;
    }

    if (this.isOpen) {
      this.shrink();
    } else {
      this.open();
    }
  }

  private shrink() {
    this.isClosing = true;
    const startHeight = `${this.item.offsetHeight}px`;
    const endHeight = `${this.accordionTrigger.offsetHeight}px`;

    if (this.animation) {
      this.animation.cancel();
    }

    this.animation = this.item.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: 400,
        easing: 'ease-out',
      },
    );

    this.animation.onfinish = () => {
      this.isOpen = false;
      this.onAnimationFinish();
    };

    this.animation.oncancel = () => {
      this.isClosing = false;
    };
  }

  private open() {
    this.item.style.height = `${this.item.offsetHeight}px`;
    this.isOpen = true;
    window.requestAnimationFrame(() => this.expand());
  }

  private expand() {
    this.isExpanding = true;
    const startHeight = `${this.item.offsetHeight}px`;
    const endHeight = `${this.accordionTrigger.offsetHeight + this.accordionPanel.offsetHeight}px`;

    if (this.animation) {
      this.animation.cancel();
    }

    this.animation = this.item.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: 400,
        easing: 'ease-out',
      },
    );

    this.animation.onfinish = () => {
      this.isOpen = true;
      this.onAnimationFinish();
    };
    this.animation.oncancel = () => {
      this.isExpanding = false;
    };
  }

  private onAnimationFinish() {
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.item.style.overflow = '';
    this.item.style.height = this.isOpen
      ? 'auto'
      : `${this.accordionTrigger.offsetHeight}px`;
  }

  render() {
    return <Host>{this.renderAccordionRow()}</Host>;
  }
}
